import { t } from '@/lib/i18n/messages.js';
import { cn } from '@/lib/utils.js';
import type { VisualizationKind } from '@quartup/reports-shared';
import { useEffect, useRef } from 'react';

interface ChartPanelProps {
  rows: ReadonlyArray<Record<string, unknown>>;
  visualization: VisualizationKind;
  categoryKey?: string;
  valueKey?: string;
  label?: string;
}

const PRIMARY_RED = '#DC2626';
const CHART_PALETTE = [PRIMARY_RED, '#3B82F6', '#22C55E', '#EAB308', '#8B5CF6', '#F97316'];

const IMPLEMENTED_KINDS: ReadonlySet<VisualizationKind> = new Set([
  'bar',
  'line',
  'area',
  'pie',
  'donut',
]);

function interpolateMsg(template: string, values: Record<string, string>): string {
  return template.replace(/\{(\w+)\}/g, (_, key: string) => values[key] ?? '');
}

// ---------------------------------------------------------------------------
// Inner component — owns the ECharts hooks; only rendered for implemented kinds
// ---------------------------------------------------------------------------

interface ChartCanvasProps {
  rows: ReadonlyArray<Record<string, unknown>>;
  visualization: VisualizationKind;
  categoryKey: string | undefined;
  valueKey: string | undefined;
}

function ChartCanvas({ rows, visualization, categoryKey, valueKey }: ChartCanvasProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  // biome-ignore lint/suspicious/noExplicitAny: echarts uses any-typed instance
  const chartRef = useRef<any>(null);

  useEffect(() => {
    if (containerRef.current === null) return;
    if (typeof window === 'undefined') return;

    let disposed = false;

    import('echarts').then((echarts) => {
      if (disposed || containerRef.current === null) return;

      if (chartRef.current === null) {
        chartRef.current = echarts.init(containerRef.current, null, { renderer: 'canvas' });
      }

      const chart = chartRef.current;

      const cats = categoryKey !== undefined ? rows.map((r) => String(r[categoryKey] ?? '')) : [];
      const vals =
        valueKey !== undefined
          ? rows.map((r) => {
              const v = r[valueKey];
              return typeof v === 'number' ? v : Number(v) || 0;
            })
          : [];

      // biome-ignore lint/suspicious/noExplicitAny: echarts option type
      let option: any;

      if (visualization === 'pie' || visualization === 'donut') {
        option = {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'item' },
          series: [
            {
              type: 'pie',
              radius: visualization === 'donut' ? ['40%', '70%'] : '70%',
              data: rows.map((r, i) => ({
                name: String(r[categoryKey ?? ''] ?? ''),
                value:
                  typeof r[valueKey ?? ''] === 'number'
                    ? r[valueKey ?? '']
                    : Number(r[valueKey ?? '']) || 0,
                itemStyle: { color: CHART_PALETTE[i % CHART_PALETTE.length] },
              })),
            },
          ],
        };
      } else if (visualization === 'line' || visualization === 'area') {
        option = {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'axis' },
          xAxis: { type: 'category', data: cats, axisLabel: { color: '#9CA3AF' } },
          yAxis: { type: 'value', axisLabel: { color: '#9CA3AF' } },
          grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
          series: [
            {
              type: visualization === 'area' ? 'line' : 'line',
              areaStyle: visualization === 'area' ? { opacity: 0.15 } : undefined,
              data: vals,
              lineStyle: { color: PRIMARY_RED },
              itemStyle: { color: PRIMARY_RED },
              smooth: true,
            },
          ],
        };
      } else {
        // bar (default for all remaining implemented kinds)
        option = {
          backgroundColor: 'transparent',
          tooltip: { trigger: 'axis' },
          xAxis: {
            type: 'category',
            data: cats,
            axisLabel: { color: '#9CA3AF', rotate: cats.length > 8 ? 30 : 0 },
          },
          yAxis: { type: 'value', axisLabel: { color: '#9CA3AF' } },
          grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
          series: [
            {
              type: 'bar',
              data: vals,
              itemStyle: { color: PRIMARY_RED, borderRadius: [3, 3, 0, 0] },
            },
          ],
        };
      }

      chart.setOption(option);
    });

    const observer = new ResizeObserver(() => {
      chartRef.current?.resize();
    });
    if (containerRef.current !== null) {
      observer.observe(containerRef.current);
    }

    return () => {
      disposed = true;
      observer.disconnect();
      chartRef.current?.dispose();
      chartRef.current = null;
    };
  }, [rows, visualization, categoryKey, valueKey]);

  return <div ref={containerRef} className="h-[300px] w-full" aria-hidden="true" />;
}

// ---------------------------------------------------------------------------
// Public component
// ---------------------------------------------------------------------------

export function ChartPanel({ rows, visualization, categoryKey, valueKey, label }: ChartPanelProps) {
  const isImplemented = IMPLEMENTED_KINDS.has(visualization);

  return (
    <div
      className={cn(
        'rounded-[var(--radius-card)] border border-[var(--color-qup-border-dark)]',
        'bg-[var(--color-qup-bg-card-dark)] p-4',
      )}
    >
      {label !== undefined && (
        <p className="mb-3 text-sm font-medium text-[var(--color-qup-text-dark)]">{label}</p>
      )}

      {isImplemented ? (
        <ChartCanvas
          rows={rows}
          visualization={visualization}
          categoryKey={categoryKey}
          valueKey={valueKey}
        />
      ) : (
        <div
          className="flex h-[300px] w-full items-center justify-center"
          data-testid="chart-unsupported-placeholder"
        >
          <p className="text-sm text-[var(--color-qup-text-muted-dark)]">
            {interpolateMsg(t('chart.unsupported_kind'), { kind: visualization })}
          </p>
        </div>
      )}
    </div>
  );
}
